import { PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Alert, Input, Space } from 'antd';
import { useCallback } from 'react';
import styled from 'styled-components';
import { type Widget } from '../../../../Widget.ts';
import {
  type ITableCell,
  type ITableWidgetCell,
  TableCellType,
} from '../ITableCell.ts';
import { TableCellDraggable } from './TableCellDraggable.tsx';
import { TableCellWidgetEditDrawer } from './TableCellWidgetEditDrawer.tsx';
import { TableColumnCellDroppable } from './TableColumnCellDroppable.tsx';

const $ColumnsSpace = styled(Space)`
  width: 100%;
  margin-bottom: 12px;
`;

export interface IRowCustomCellWidgetListProps {
  name: string;
  columns?: Widget[];
}

const isWidgetCellWithId =
  (id: UniqueIdentifier | null | undefined) =>
  (item: ITableCell): item is ITableWidgetCell =>
    item.type === TableCellType.Widget && item.widget.id === id;

/**
 * Trova tutti i widget posizionati nella colonna indicata
 */
const isWidgetCellInColumn =
  (columnId: UniqueIdentifier | null | undefined) =>
  (item: ITableCell): item is ITableWidgetCell =>
    item.type === TableCellType.Widget && item.columnId === columnId;

/**
 * Componente
 */
export function TableCellWidgetList(props: IRowCustomCellWidgetListProps) {
  const { name, columns } = props;

  const editor = useRelationEditor<ITableCell>(name);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!columns || !over) return;

      if (active.id !== over?.id) {
        const activeItem = editor.items.find(isWidgetCellWithId(active.id));
        const otherItems = editor.items.filter(isWidgetCellInColumn(over?.id));

        if (!activeItem) return;

        const targetColumn = columns.find(c => c.id === over?.id)!;
        editor.update(activeItem, { ...activeItem, columnId: targetColumn.id });

        // Se abbiamo trovato un altro widget nella colonna di destinazione,
        // lo spostiamo nei Widget da posizionare.
        if (otherItems) {
          // biome-ignore format: <tbd>
          console.log('[TableCell] Sposto fuori le celle concomitanti nella stessa colonna', otherItems);
          otherItems.forEach(otherItem => {
            editor.update(otherItem, { ...otherItem, columnId: null });
          });
        }
      }
    },
    [editor, columns],
  );

  const unplacedCells = editor.items.filter(item =>
    item.type !== TableCellType.Widget
      ? false
      : !item.columnId || !columns?.some(column => column.id === item.columnId),
  ) as ITableWidgetCell[];

  return (
    <div>
      {/* {editor.items.length < 1 && (
        <Alert
          type="warning"
          showIcon
          message="Aggiungere almeno una Colonna"
        />
      )} */}

      <FormItem name={name} label={null}>
        <DndContext onDragEnd={handleDragEnd}>
          {!columns ||
            (columns.length === 0 && (
              <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                message="Nessuna colonna disponibile per il posizionamento"
                description="È necessario aggiungere almeno una Colonna per posizionare le Celle personalizzate. Una volta aggiunte le colonne è possibile trascinarle tramite Drag&Drop sulla colonna in cui devono essere mostrate."
              />
            ))}
          {columns?.map((column, index) => {
            const placedCell = editor.items.find(
              item =>
                item.type === TableCellType.Widget &&
                item.columnId === column.id,
            );

            return (
              <TableColumnCellDroppable
                key={column.id}
                column={column}
                placedCell={placedCell}
                handleEdit={() => {
                  editor.handle.open(placedCell!);
                }}
                handleRemove={() => {
                  editor.remove(placedCell!);
                }}
                // handleAdd={selectedType => {
                //   const widgetType = WidgetRepository.find(selectedType);
                //   if (!widgetType) return;

                //   const widget = WidgetRepository.getInitial(
                //     widgetType.type,
                //     0,
                //     {
                //       label: [{ type: 'text', text: 'Nuova cella' }],
                //       code: `cella_${editor.items.length}`
                //     }
                //   );

                //   if (!widget) return;

                //   editor.handle.open({
                //     type: TableCellType.Widget,
                //     columnId: column.id,
                //     widget
                //   });
                // }}
              />
            );
          })}
          {unplacedCells.length > 0 && <h3>Celle non Posizionate</h3>}
          <$ColumnsSpace direction="vertical">
            {unplacedCells.map((cell, index) => (
              <TableCellDraggable
                key={cell.widget.id}
                cell={cell}
                handleEdit={() => {
                  editor.handle.open(cell);
                }}
                handleRemove={() => {
                  editor.remove(cell);
                }}
              />
            ))}
          </$ColumnsSpace>
        </DndContext>

        {/* Pulsanti da aggiunta */}
        <Input.Group compact>
          <TableCellWidgetEditDrawer
            editor={editor}
            buttonProps={{
              type: 'dashed',
              size: 'large',
              icon: <PlusOutlined aria-hidden="true" />,
              style: { width: '40%' },
              children: 'Aggiungi Campo',
            }}
          />
        </Input.Group>
      </FormItem>
    </div>
  );
}
